import { action, computed, makeObservable, observable } from 'mobx';
import { mapTabs } from 'src/api/new-well/serializers/common-serializers';
import {
  serializeFormForRequest,
  serializeRigOperations,
} from 'src/api/new-well/serializers/serialize-form-for-request';
import { TFormRaw, TIdTriple, TSerializedForm } from 'src/api/new-well/types';
import { assert } from 'src/shared/utils/assert';
import { hasValue } from 'src/shared/utils/common';
import { equalsDeep } from 'src/shared/utils/equals-deep';
import { EventBus } from 'src/shared/utils/event-bus';
import { getRandomNumber } from 'src/shared/utils/get-random-number';
import { parsePrimitivesFromString } from 'src/shared/utils/parse-primitives-from-string';
import { RootStore } from 'src/store';

import { Item, Tab, ValidatableItem } from '../../../shared/entities/abstract-control-entities';
import { Label } from '../../../shared/entities/control-entities';
import { FormPlugin } from '../plugins';
import { ValidationManager } from '../validation/validation-manager';

import { ColumnTab } from './column-tab.entity';
import { FormDirectoryValuesService } from './form-directory-values-service';
import { FormMessagesService } from './form-messages-service/form-messages-service';
import { GeologicalLoadService } from './geological-load/geological-load-service';
import { RigOperation } from './rig-operations/rig-operation.entity';
import { RigOperationsService } from './rig-operations/rig-operations-service';
import { TSerializedFormValues } from './types';

export type TProcessFormFieldsData = {
  excludeApproachesTab?: boolean;
  excludeDisabledTabs?: boolean;
};

export interface IFormEvents {
  mainDataValueChange: {
    fieldId: string;
    value: unknown;
    control: Item;
  };
}

function checkIsValueValid(value: unknown, excludeFalsyValues: boolean): boolean {
  if (!excludeFalsyValues) {
    return true;
  } else {
    if (value !== null && value !== 'null' && value !== undefined && value !== 'undefined') {
      return true;
    }
    return false;
  }
}

export class FormStore {
  @observable tabs: Tab[] = [];
  @observable activeTabId: string = 'mainData';
  @observable activeTab: Tab | null = null;
  @observable isEditingMode = false;
  @observable isPluginsInitialized = false;
  @observable _isLoading = false;
  @observable pendingEvents: number[] = [];
  @observable isUnplanned = false;
  @observable isReadOnlyMode = false;

  readonly initialFormData: Record<string, unknown> | null;
  readonly eventBus = new EventBus<IFormEvents>();
  readonly formMessagesService: FormMessagesService;
  readonly geologicalLoadService: GeologicalLoadService;
  readonly rigOperationsService: RigOperationsService;
  readonly formDirectoryValuesService: FormDirectoryValuesService;
  fields: Record<string, Item> = {};
  readonly id: number;
  readonly index: number;
  readonly validationManager: ValidationManager;
  readonly plugins: FormPlugin[];
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore, initialValues: Record<string, unknown> | null, plugins: FormPlugin[] = []) {
    this.id = getRandomNumber();
    this.index = FormStore.getWellIndex(initialValues) ?? 0;
    this.plugins = plugins;
    this.rootStore = rootStore;
    this.initialFormData = initialValues;
    this.validationManager = new ValidationManager(this, this.rootStore);
    this.formDirectoryValuesService = new FormDirectoryValuesService(this, this.rootStore);
    this.formMessagesService = new FormMessagesService();
    this.geologicalLoadService = new GeologicalLoadService(rootStore, this.eventBus, initialValues);
    this.rigOperationsService = new RigOperationsService(
      rootStore,
      this,
      this.formDirectoryValuesService,
      this.geologicalLoadService,
      this.eventBus,
      initialValues,
    );

    makeObservable(this);
  }

  static getWellIndex(wellData: Record<string, unknown> | null): number | null {
    if (!wellData) {
      return null;
    }

    const rawIndex = parsePrimitivesFromString(wellData['GOplan_GeologicalTask.index']);

    if (!hasValue(rawIndex) || typeof rawIndex !== 'number') {
      return null;
    }

    return rawIndex;
  }

  private connectPluginsWithForm(): VoidFunction {
    const disposers: VoidFunction[] = [];

    this.plugins.forEach((plugin) => {
      const disposer = plugin.connect(this);
      if (disposer) {
        disposers.push(disposer);
      }
    });

    this.setIsPluginsInitialized(true);

    return () => {
      disposers.forEach((disposer) => disposer());
    };
  }

  @action.bound
  private setIsPluginsInitialized(is: boolean) {
    this.isPluginsInitialized = is;
  }

  @computed
  get isLoading(): boolean {
    return this._isLoading || !!this.pendingEvents.length;
  }

  @action.bound
  setMainDataDirectoryValue(fieldId: string, control: Item) {
    // const directoryValue = this.formDirectoryValuesService.directoryValues.get('mainData')?.[fieldId];
    const directoryValue = (() => {
      switch (fieldId) {
        case 'passingValue': {
          return this.formDirectoryValuesService.getMainDataDirPassingValue();
        }
        case 'commercialSpeed': {
          return this.formDirectoryValuesService.getMainDataDirCommercialSpeed();
        }
      }
    })();

    if (!hasValue(directoryValue)) {
      return;
    }

    this.onValueChange(fieldId, directoryValue, { control });
  }

  @action.bound
  onValueChange(fieldId: string, value: unknown, { control }: { control: Item }): void {
    if (equalsDeep(value, control.value)) {
      return;
    }

    const isSuccess = control.tryToSetRawValue(value);

    if (isSuccess) {
      this.eventBus.publish('mainDataValueChange', { fieldId, value, control });
    }
  }

  @action.bound
  setIsLoading(is: boolean): void {
    this._isLoading = is;
  }

  @action.bound
  addPendingEvent(id: number): void {
    this.pendingEvents.push(id);
  }

  @action.bound
  removePendingEvent(id: number): void {
    const pendingEventIndex = this.pendingEvents.indexOf(id);
    if (pendingEventIndex >= 0) {
      this.pendingEvents.splice(pendingEventIndex, 1);
    }
  }

  @computed
  get mainDataTab(): ColumnTab {
    const mainDataTab = this.tabs.find((tab): tab is ColumnTab => tab.formElementRefId === 'mainData');

    assert(mainDataTab, 'mainDataTab is not presented');

    return mainDataTab;
  }

  @action.bound
  setIsEditingMode(is: boolean): void {
    this.isEditingMode = is;
  }

  @action.bound
  setRigOperationsIds(ids: TIdTriple[]): void {
    const rigOperationIds = ids.filter((triple) => triple.objectType === 'GOplan_RigOperation');

    if (rigOperationIds.length === 1 && !rigOperationIds[0].oldId) {
      const newRigOperationId = rigOperationIds[0].id;

      this.rigOperationsService.rigOperations.forEach((rigOperation) => {
        rigOperation.setRigOperationId(newRigOperationId);
      });
    } else {
      this.rigOperationsService.rigOperations.forEach((rigOperation) => {
        const newRigOperationId = rigOperationIds.find((rigOpId) => {
          if (rigOperation.rigOperationId) {
            return rigOperation.rigOperationId === rigOpId.oldId;
          } else {
            return rigOpId.oldId === null;
          }
        })?.id;

        if (newRigOperationId) {
          rigOperation.setRigOperationId(newRigOperationId);
        }
      });
    }
  }

  @action.bound
  setActiveTab(id: string): void {
    this.activeTabId = id;
    const newActiveTab = this.tabs.find((tab) => tab.formElementRefId === id);

    if (newActiveTab) {
      this.activeTab = newActiveTab;
    }
  }

  private collectFields(): Record<string, Item> {
    const fields: Record<string, Item> = {};

    const collect = (item: Item) => {
      fields[item.fieldId] = item;
      if (item.formElementRefId) {
        fields[item.formElementRefId] = item;
      }
    };

    this.processFormFields(collect);

    return fields;
  }

  processFormFields = (callback: (item: Item) => void, options?: TProcessFormFieldsData): void => {
    for (const tab of this.tabs) {
      if (options?.excludeDisabledTabs ? !tab.isDisabled : true) {
        if (tab instanceof ColumnTab) {
          for (const column of tab.columns) {
            for (const item of column.items) {
              callback(item);
            }
          }
        }
      }
    }
  };

  @computed
  get progressBarFieldsCount(): [number, number] {
    let useInProgressberFieldsCount = 0;
    let filledFieldsCount = 0;

    const checkDoFieldCounts = (item: Item): void => {
      if (!item.isDisabled) {
        if (item instanceof ValidatableItem) {
          if (item.useInMainProgressBar) {
            useInProgressberFieldsCount++;
            if (item.isReady) filledFieldsCount++;
          }
        }
      }
    };
    this.processFormFields(checkDoFieldCounts, { excludeDisabledTabs: true });

    return [useInProgressberFieldsCount, filledFieldsCount];
  }

  @action.bound
  setServerErrors(errors?: Record<string, string>): void {
    const setError = (item: Item): void => {
      if (!item.isDisabled) {
        if (item instanceof ValidatableItem) {
          if (!!errors && item.fieldId in errors) {
            item.setError(errors[item.fieldId]);
          }
        }
      }
    };

    this.processFormFields(setError, { excludeDisabledTabs: true });
  }

  @action.bound
  reset(): void {
    const resetControl = (item: Item) => {
      item.clearItem();
    };

    this.processFormFields(resetControl, { excludeDisabledTabs: true });
    this.rigOperationsService.setRigOperations([]);
  }

  @action.bound
  setIsReadOnlyMode(is: boolean): void {
    this.isReadOnlyMode = is;
  }

  @action.bound
  setFormValues(values: TSerializedFormValues, excludeFalsyValues = false): void {
    for (const [key, fieldValue] of Object.entries(values)) {
      const control = this.fields[key];

      if (!control) {
        continue;
      }

      const isValidValue = checkIsValueValid(fieldValue, excludeFalsyValues);

      if (
        !isValidValue ||
        control.tryToSetRawValue(fieldValue, true) ||
        fieldValue === undefined ||
        control instanceof Label
      ) {
        control.setInitialValue(control.value);
      }
    }
    // //TODO: Отрефакторить данный метод после рефакторинга генерации подходов, этапов и секций.
    // const approaches = values.GOplan_RigOperations || values['GOplan_RigOperation.approaches'];
    // if (approaches?.length) {
    //   const approachesList = this.approachesTab?.approachesList;
    //   if (!approachesList) return;
    //   const approachesControls: Approach[] = [];

    //   approaches
    //     .sort(
    //       (firstAppr, secondAppr) =>
    //         Number(firstAppr[approachesList.approachReference.indexAttrName]) -
    //         Number(secondAppr[approachesList.approachReference.indexAttrName])
    //     )
    //     .forEach((rawApproach) => {
    //       if (isObjectWithKeys(rawApproach)) {
    //         const id = rawApproach[approachesList.approachReference.rigOperationIdAttrName] || rawApproach.id;

    //         const existingApproach = this.approachesTab?.approachesList.approaches.find((a) => a.id === id);

    //         if (existingApproach) {
    //           existingApproach.stagesList.setStagesByRawData(rawApproach['GOplan_RigOperation.stages']);

    //           approachesControls.push(existingApproach);
    //         } else {
    //           const newApproach = mapApproach(
    //             {
    //               approachReference: approachesList.approachReference,
    //               directories: this.rootStore.directories,
    //               eventBus: this.eventBus,
    //             },
    //             rawApproach
    //           );

    //           approachesControls.push(newApproach);
    //         }
    //       }
    //     });
    //   approachesList.setApproaches(approachesControls);
    // }
  }

  serializeRigOperations(rigOperations: RigOperation[]) {
    return serializeRigOperations(rigOperations, this.geologicalLoadService.geoLoadRigOperationsByRigOperationIdMap);
  }

  getSerializedForm = (): TSerializedForm => {
    return serializeFormForRequest(this);
  };

  @action.bound
  init(formView: TFormRaw, savedForm: Record<string, unknown> | null): VoidFunction | void {
    if (this.isPluginsInitialized) {
      return;
    }

    const formDirectoryValuesServiceDisposer = this.formDirectoryValuesService.init();
    const geologicalLoadServiceDisposer = this.geologicalLoadService.init();
    const rigOperationsServiceDisposer = this.rigOperationsService.init();

    const tabs = mapTabs(formView.tabs, savedForm, this.rootStore.directories, this.eventBus);

    this.tabs = tabs;
    this.activeTab = tabs[0];
    this.fields = this.collectFields();

    const pluginsDisposer = this.connectPluginsWithForm();

    return () => {
      pluginsDisposer();
      geologicalLoadServiceDisposer();
      rigOperationsServiceDisposer();
      formDirectoryValuesServiceDisposer();
    };
  }
}
